import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { ActionMenuDataType } from "@emisgroup/cohort-builder-kit";
import { DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenu } from "@emisgroup/ui-dropdown-menu";
import { Button } from "@emisgroup/ui-button";
import { IRootState } from "../../../../../reducer/CombineReducer";
import { ProgressSpinnerSlice } from "../../../../ProgressSpinner/Index";
import { BandsTypes } from "../../Banding/Types";
import { OutputConfigRemove } from "../../OutputConfigLandingPage/OutputConfigList/RemoveOutputConfig.Logic";
import { DeleteTypes } from "../../OutputConfigLandingPage/OutputConfigList/Constants";
import { OutputMappingItem } from "../OutputMappingList/Types";
import {
  OutputMappingChildCardsForBandingsView
} from "../OutputMappingChildCardsForBandings/OutputMappingChildCardsForBandings.view";
import "../OutputMappingChildCard/Styles.scss";
import {
  Checkbox,
} from "../../../../Common/UI/Index";
import { OUTPUT_CONFIG_MAP_HIGHLIGHT, OUTPUT_CONFIG_MAP_HIGHLIGHT_PARENT } from "../../Constant";
import { removeHighlight } from "../../helper";
import Edit from "~icons/ic/outline-edit";
import TriangleDown from "~icons/ic/outline-arrow-drop-down";

interface Props {
  /**
   * Config item
   */
  mappedConfig: OutputMappingItem;
  /**
   * Action to happen on selecting band
   */
  onSelectBand: (band: string) => void;
  /**
   * Action for edit config detail
   */
  onEditConfigDetail: () => void;
  /**
   * On Toggle select
   */
  onToggleButtonSelect: (value: number) => void;
  /**
   * Selected Toggle
   */
  selectedToggle: number;
  /**
   * Action on pick report
   */
  onPickReport: () => void;
  /**
   * Current mapped config index
   */
  currentMapIndex: number;
}

/**
 * Responsible to render list card
 * @returns list card view
 */
export const OutputMappingListCardView: React.FC<Props> = (props: Props) => {
  const {
    mappedConfig,
    onSelectBand,
    onEditConfigDetail,
    onToggleButtonSelect,
    selectedToggle,
  } = props;
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const outputConfig = useSelector((state: IRootState) => state.outputConfig);
  const showBandings = outputConfig.mappingFilters.showBandings;
  const [splitDropdownMenuOpen, setSplitDropdownMenuOpen] = useState(false);
  const { tag, sourceReport } = mappedConfig;
  const reportName = sourceReport?.reportName;
  const { focusConfigId } = outputConfig;

  /**
   * Method to close the dropdown menu
   */
  const dropdownAwayClick = (isOpen: boolean): void => {
    setSplitDropdownMenuOpen(isOpen);
  };

  /**
   * Hide dropdown on scroll
   */
  const handleScroll = (): void => {
    setSplitDropdownMenuOpen(false);
  };

  /**
   * Action to scroll when final output id is found
   */
  const handleScrollIntoView: (isParentAlone: boolean) => Promise<void> = async (isParentAlone: boolean) => {
    const element = document.getElementById("ebt-outputConfig-config-" + mappedConfig.configMapId);
    if (element) {
      dispatch(ProgressSpinnerSlice.actions.showProgressBar(translate("LoadingMessage.FindingConfig")));
      await new Promise((resolve) => setTimeout(resolve, 3000));
      const className = isParentAlone ? OUTPUT_CONFIG_MAP_HIGHLIGHT_PARENT : OUTPUT_CONFIG_MAP_HIGHLIGHT;
      element.classList.add(className);
      element.scrollIntoView({ behavior: "smooth", block: "start" });

      await new Promise<void>((resolve) => {
        setTimeout(() => {
          resolve();
        }, 1000);
      });

      dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
      if (isParentAlone)
        removeHighlight(element, className, dispatch);
    }
  };

  /**
   * Register scroll event
   */
  useEffect(() => {
    window.addEventListener("scroll", handleScroll, true);
    if (focusConfigId) {
      if (mappedConfig.configMapId === focusConfigId) {
        handleScrollIntoView(true);
      }
    }

    // Remove the event listener
    return (): void => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  /**
   * Dropdown content
   */
  const drpDwnData: ActionMenuDataType[] = [
    {
      id: "1",
      name: BandsTypes.AGE,
      onClick: (): void => onSelectBand(BandsTypes.AGE)
    },
    {
      id: "2",
      name: BandsTypes.GENDER,
      onClick: (): void => onSelectBand(BandsTypes.GENDER)
    },
    {
      id: "3",
      name: BandsTypes.ETHNICITY,
      onClick: (): void => onSelectBand(BandsTypes.ETHNICITY)
    },
  ];

  const passToPrev = (): void => {
    return;
  };

  return (
    <div>
      <div
        className="output-config__card"
        id={"ebt-outputConfig-config-" + mappedConfig.configMapId}
      >
        <div
          className="output-config__grid-container"
          id="ebt-outputConfig-config-gridContainer"
        >
          <div
            className="ebt-mapping-container"
            id="ebt-outputConfig-config-gridContainer-tag"
          >
            <strong
              className="ebt-mapping-output"
              id="ebt-outputConfig-config-gridContainer-tag-name"
            >
              {translate("OutputConfig.OutputMapping.OutputTagName")}
            </strong>
            <div className="ebt-mapping-details" title={tag}>{tag}</div>
          </div>
          <div
            className="ebt-mapping-container ebt-mapping-reportname"
            id="ebt-outputConfig-config-gridcontainer-report"
          >
            <strong
              className="ebt-mapping-output"
              id="ebt-outputConfig-config-gridContainer-reportName"
            >
              {translate("OutputConfig.OutputMapping.Report")}
            </strong>
            <div className="ebt-mapping-details" title={reportName}>
              {reportName || "-"}
            </div>
          </div>
          <div>
            <Checkbox
              className="ebt-mapping__include-in-xml"
              id={"ebt-outputConfig-config-includexml-" + mappedConfig.configMapId}
              labelText={translate("OutputConfig.OutputMappingAgeForm.IncludeInOutput")}
              name="includeInOutput"
              checked={mappedConfig.includeInOutput}
              disabled={true}
            />
          </div>
          <div className="output-config__related_parent">
            <DropdownMenu
              onOpenChange={(data): void => dropdownAwayClick(data)}
              open={splitDropdownMenuOpen}
            >
              <DropdownMenuTrigger
                className="ebt-opcard-trigger"
              >
                <Button className="ebt-opcard-bandbtn">
                  {translate("OutputConfig.OutputMappingButtons.ManageBandings")}
                  <TriangleDown title="Expand" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="ebt-op-mapping-content"
                data-testid="ebt-op-mapping-content"
              >
                {drpDwnData.map((menu: ActionMenuDataType) => {
                  return (
                    <DropdownMenuItem
                      key={menu.id}
                      onClick={menu?.onClick}
                    >

                      {menu.name}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="make-flex flex-reverse">
            <div className="ebt-output-config__list__delete-icon">
              <OutputConfigRemove
                deleteType={DeleteTypes.DELETE_OUTPUT_MAP_LIST}
                mapId={mappedConfig.configMapId}
                mapName={mappedConfig.configName}
              />
            </div>
            <Edit
              title="edit"
              className="ebt-output-config__list__edit-icon"
              onClick={onEditConfigDetail}
              id="ebt-outputConfig-config-mapping-edit"
            />
          </div>
        </div>
        {
          showBandings &&
          <OutputMappingChildCardsForBandingsView
            mappingChildItem={mappedConfig}
            layerId={0}
            onToggleButtonSelect={onToggleButtonSelect}
            selectedToggle={selectedToggle}
            show={!!focusConfigId}
            passToPrev={passToPrev}
          />
        }
      </div>
    </div >
  );
};
